import { isAxiosError } from 'axios';
import type { AxiosRequestConfig } from 'axios';
import axiosInstance, { getAccessToken } from './axiosInstance';

type AxiosBaseQueryProps = {
  baseUrl?: string;
};

type AxiosInstanceProps = {
  url: string;
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  data?: unknown;
  params?: AxiosRequestConfig['params'];
  headers?: AxiosRequestConfig['headers'];
  responseType?: 'json' | 'blob' | 'text';
  includeAuth?: boolean;
};

export interface IErrorResponse<T> {
  status: number | undefined;
  data: T | null;
}

export const axiosBaseQuery =
  <T = unknown>({ baseUrl = '' }: AxiosBaseQueryProps = {}) =>
  async ({
    url,
    method = 'GET',
    data,
    params,
    headers = {},
    responseType = 'json',
    includeAuth = true,
  }: AxiosInstanceProps) => {
    try {
      const result = await axiosInstance({
        url: baseUrl + url,
        method,
        data,
        params,
        headers:
          data instanceof FormData
            ? { ...headers }
            : {
                'Content-Type': 'application/json',
                ...headers,
                ...(includeAuth && getAccessToken() ? { Authorization: `Bearer ${getAccessToken()}` } : {}),
              },
        responseType,
      });

      return { data: result.data };
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        return {
          error: {
            status: error.response?.status,
            data: error.response?.data || null,
          } as IErrorResponse<T>,
        };
      }
      return Promise.reject(new Error('An unexpected error occurred.'));
    }
  };

export default axiosBaseQuery;
